import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

export interface ApiResponse<T> {
  data: T;
  message?: string;
  status: number;
}

class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private refreshQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
  }> = [];
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      // Send the httpOnly `jarvis_refresh` cookie on auth calls (same-origin).
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Skip if this is a retry with an already-set token
        if ((config as any)._skipInterceptor) return config;
        const token = useAuthStore.getState().token;
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        const originalRequest = error.config;

        const status = error.response?.status;

        // On 429 (rate limited), wait and retry once instead of failing
        if (status === 429 && !originalRequest._rateLimitRetry) {
          originalRequest._rateLimitRetry = true;
          await new Promise((r) => setTimeout(r, 2000));
          return this.client(originalRequest);
        }

        if (status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          if (this.isRefreshing) {
            return new Promise<string>((resolve, reject) => {
              this.refreshQueue.push({ resolve, reject });
            }).then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              // Remove the request interceptor override for this retry
              originalRequest._skipInterceptor = true;
              return this.client(originalRequest);
            });
          }

          this.isRefreshing = true;

          try {
            // The refresh token lives in the httpOnly `jarvis_refresh` cookie
            // (sent via withCredentials), so we no longer require an in-memory
            // token — that's what lets a page reload re-mint an access token.
            // If a memory token is present we still pass it (transition/no-cookie
            // fallback).
            const refreshToken = useAuthStore.getState().refreshToken;

            // Retry refresh up to 3 times on network errors
            let response;
            for (let attempt = 0; attempt < 3; attempt++) {
              try {
                response = await axios.post(
                  `${API_BASE_URL}/auth/refresh`,
                  refreshToken ? { refresh_token: refreshToken } : {},
                  { withCredentials: true },
                );
                break; // success
              } catch (retryErr: any) {
                const retryStatus = retryErr?.response?.status;
                // Don't retry on definitive auth failures
                if (retryStatus === 401 || retryStatus === 403) throw retryErr;
                // Retry on network errors, 429, 500, 502, 503, 504
                if (attempt < 2) {
                  await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
                  continue;
                }
                throw retryErr;
              }
            }

            const { access_token, refresh_token } = response!.data;
            useAuthStore.getState().setToken(access_token);
            useAuthStore.getState().setRefreshToken(refresh_token);
            this.scheduleTokenRefresh(access_token);

            this.refreshQueue.forEach(({ resolve }) => resolve(access_token));
            this.refreshQueue = [];

            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            return this.client(originalRequest);
          } catch (refreshError: any) {
            this.refreshQueue.forEach(({ reject }) => reject(refreshError));
            this.refreshQueue = [];
            const refreshStatus = refreshError?.response?.status;
            // Only logout on definitive auth failures (401/403 from refresh endpoint)
            // NOT on network errors, 429, 500, etc.
            if (refreshStatus === 401 || refreshStatus === 403) {
              useAuthStore.getState().logout();
              // Use soft redirect, not hard reload
              if (window.location.pathname !== '/login') {
                window.location.replace('/login');
              }
            }
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Schedule a proactive token refresh before it expires.
   * Call after login or any successful token refresh.
   */
  scheduleTokenRefresh(token?: string) {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);

    const t = token || useAuthStore.getState().token;
    if (!t) return;

    try {
      const payload = JSON.parse(atob(t.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      const now = Date.now();
      // Refresh 5 minutes before expiry, minimum 30 seconds from now
      const refreshIn = Math.max((expiresAt - now) - 5 * 60 * 1000, 30_000);

      this.refreshTimer = setTimeout(async () => {
        try {
          // Cookie-based refresh (httpOnly); pass the memory token if present.
          const refreshToken = useAuthStore.getState().refreshToken;
          const response = await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            refreshToken ? { refresh_token: refreshToken } : {},
            { withCredentials: true },
          );
          const { access_token, refresh_token } = response.data;
          useAuthStore.getState().setToken(access_token);
          useAuthStore.getState().setRefreshToken(refresh_token);
          this.scheduleTokenRefresh(access_token);
        } catch {
          // Proactive refresh failed — the 401 interceptor will handle it reactively
        }
      }, refreshIn);
    } catch {
      // Token decode failed — ignore
    }
  }

  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await this.client.get<T>(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.post<T>(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.patch<T>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url);
    return response.data;
  }

  async postFormData<T>(url: string, formData: FormData): Promise<T> {
    const response = await this.client.post<T>(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getPreferences(): Promise<Record<string, unknown>> {
    return this.get<Record<string, unknown>>('/auth/me/preferences');
  }

  async updatePreferences(prefs: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.put<Record<string, unknown>>('/auth/me/preferences', prefs);
  }
}

export const api = new ApiClient();

// On app load, schedule proactive refresh if we already have a token
api.scheduleTokenRefresh();
